import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  Phone,
  Calendar,
  Settings,
  Sun,
  PhoneCall,
  Plug,
} from 'lucide-react';

const STORAGE_KEY = 'merso_onboarding_seen_v1';

const pages = [
  { icon: BarChart3, label: 'Özet Panel', desc: 'Canlı çağrı istatistikleri ve günlük özet.' },
  { icon: Phone,     label: 'Çağrı Kayıtları', desc: 'Tüm görüşmeler, özetler ve CSV dışa aktarım.' },
  { icon: Calendar,  label: 'Randevular', desc: 'Rezervasyonlar ve günlük slot takvimi.' },
  { icon: Settings,  label: 'Ayarlar', desc: 'Çalışma saatleri, kapasite ve MTS/SKS numaraları.' },
  { icon: Plug,      label: 'Entegrasyon', desc: 'Bağlantı durumu ve yapılandırma.' },
  { icon: PhoneCall, label: 'İletişim', desc: 'Destek ve geri bildirim.' },
];

function hasSeenOnboarding() {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markOnboardingSeen() {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // storage blocked — just don't show again this session
  }
}

const OnboardingModal = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!hasSeenOnboarding()) {
      // Small delay so the dashboard renders first
      const t = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, []);

  const close = () => {
    markOnboardingSeen();
    setOpen(false);
  };

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'var(--bg-overlay)' }}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl shadow-2xl"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-strong)',
        }}
      >
        {/* Header */}
        <div
          className="rounded-t-2xl px-6 py-4"
          style={{ borderBottom: '1px solid var(--border-subtle)' }}
        >
          <h2 className="text-base font-bold" style={{ color: 'var(--text-strong)' }}>
            Mengerler Demo Paneli
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Hızlı başlangıç rehberi
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Pages list */}
          <div className="space-y-2">
            {pages.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="flex items-start gap-3">
                <div
                  className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                  style={{ background: 'rgba(74,158,138,0.14)' }}
                >
                  <Icon className="h-3.5 w-3.5" style={{ color: 'var(--accent)' }} />
                </div>
                <div>
                  <span className="text-sm font-medium" style={{ color: 'var(--text-strong)' }}>
                    {label}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {' '}— {desc}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid var(--border-subtle)' }} />

          {/* Tips */}
          <div className="space-y-2 text-sm" style={{ color: 'var(--text-muted)' }}>
            <p
              className="rounded-xl px-4 py-3"
              style={{
                background: 'rgba(74,158,138,0.10)',
                border: '1px solid rgba(74,158,138,0.25)',
              }}
            >
              <span className="font-semibold" style={{ color: 'var(--accent)' }}>
                🎙 Demo — Gerçek çağrı simülasyonu:
              </span>{' '}
              Sağ alt köşedeki mikrofon butonuna tıklayın, mikrofona izin verin ve asistanla{' '}
              <span className="font-semibold" style={{ color: 'var(--text-body)' }}>Türkçe konuşun.</span>{' '}
              Randevu alabilir, iptal edebilir veya bilgi sorabilirsiniz — tıpkı gerçek bir müşteri gibi.
            </p>
            <p className="flex items-center gap-1.5">
              <Sun className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--accent)' }} />
              <span>
                <span className="font-semibold" style={{ color: 'var(--text-body)' }}>Tema:</span>{' '}
                Sağ üstteki yuvarlak buton ile açık / koyu mod arasında geçiş yapın.
              </span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between rounded-b-2xl px-6 py-4"
          style={{ borderTop: '1px solid var(--border-subtle)' }}
        >
          <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
            Bu pencere bir daha açılmayacak.
          </p>
          <button
            type="button"
            onClick={close}
            className="flex items-center gap-2 rounded-xl px-5 py-2 text-sm font-semibold text-white transition-all duration-150 hover:scale-[1.03] active:scale-100"
            style={{
              background: 'linear-gradient(135deg, #4a9e8a 0%, #38806f 100%)',
              boxShadow: '0 0 0 0 rgba(74,158,138,0)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 0 14px 2px rgba(74,158,138,0.45)')}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 0 0 0 rgba(74,158,138,0)')}
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
